import { useState, useRef, useEffect } from "react";
import { Microphone, FileAudio, FileText, Trash, Pencil, Play, Pause } from "@phosphor-icons/react";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "@/config/firebase";
import { renameTake, deleteTake } from "@/lib/firestore/takes";
import { Card, Badge, Eyebrow, Input } from "@/components/ui";
import type { Take } from "@/types";

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const TYPE_ICONS = {
  recording:    Microphone,
  audio_upload: FileAudio,
  text_upload:  FileText,
};

type StatusTone = "verified" | "in-review" | "take" | "contested" | "draft";

const STATUS_TONE: Record<string, { tone: StatusTone; label: string }> = {
  uploading:  { tone: "in-review", label: "Adding source" },
  ready:      { tone: "verified",  label: "Source ready" },
  processing: { tone: "in-review", label: "In the press" },
  error:      { tone: "contested", label: "Blocked" },
};

/**
 * TakeCard — one take in the studio lane.
 *
 * Walnut spine on the left (chapter-card pattern), paper-raised
 * surface, mono dateline, proof-state badge for status. Hover lifts
 * shadow-as-3.
 */
export function TakeCard({ take }: { take: Take }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(take.title);
  const [playing, setPlaying] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const Icon = TYPE_ICONS[take.type];
  const isAudio = take.type !== "text_upload";

  useEffect(() => {
    if (isAudio && take.status === "ready" && take.storage_path) {
      getDownloadURL(ref(storage, take.storage_path))
        .then(setDownloadUrl)
        .catch(() => setDownloadUrl(null));
    }
  }, [isAudio, take.status, take.storage_path]);

  const handleRename = async () => {
    if (title.trim() && title !== take.title) {
      await renameTake(take.project_id, take.id, title.trim());
    }
    setEditing(false);
  };
  const handleDelete = async () => {
    await deleteTake(take.project_id, take.id);
    setConfirming(false);
  };
  const togglePlay = () => {
    if (!audioRef.current || !downloadUrl) return;
    if (playing) audioRef.current.pause(); else audioRef.current.play();
    setPlaying(!playing);
  };

  const status = STATUS_TONE[take.status] ?? { tone: "draft" as StatusTone, label: take.status };

  return (
    <Card variant="chapter" interactive className="flex items-center gap-4 p-4">
      {/* Type icon plate */}
      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center bg-walnut-tint rounded-as-2">
        <Icon size={20} weight="duotone" className="text-walnut-deep" />
      </div>

      {/* Title + dateline */}
      <div className="min-w-0 flex-1">
        {editing ? (
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => e.key === "Enter" && handleRename()}
            autoFocus
          />
        ) : (
          <p className="truncate font-display font-semibold text-t-16 leading-as-snug text-ink-1">
            {take.title}
          </p>
        )}
        <div className="mt-[2px]">
          <Eyebrow>
            {isAudio
              ? `Source · ${formatDuration(take.duration_seconds)}`
              : `${take.format.toUpperCase()} pages`}
          </Eyebrow>
        </div>
      </div>

      {/* Status */}
      <Badge tone={status.tone}>{status.label}</Badge>

      {/* Actions */}
      <div className="flex items-center gap-1">
        {isAudio && take.status === "ready" && downloadUrl && (
          <>
            <IconButton
              onClick={togglePlay}
              ariaLabel={playing ? "Pause source" : "Play source"}
            >
              {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
            </IconButton>
            <audio ref={audioRef} src={downloadUrl} onEnded={() => setPlaying(false)} />
          </>
        )}
        <IconButton onClick={() => setEditing(true)} ariaLabel="Rename source">
          <Pencil size={16} />
        </IconButton>
        {confirming ? (
          <button
            onClick={handleDelete}
            className="inline-flex items-center font-mono text-[11px] tracking-as-eyebrow uppercase text-flag bg-flag-tint px-2 py-1 rounded-as-1 hover:bg-flag hover:text-paper-raised transition-colors duration-as-1"
          >
            Remove
          </button>
        ) : (
          <IconButton onClick={() => setConfirming(true)} ariaLabel="Remove source" danger>
            <Trash size={16} />
          </IconButton>
        )}
      </div>
    </Card>
  );
}

function IconButton({
  children, onClick, ariaLabel, danger = false,
}: {
  children: React.ReactNode; onClick: () => void; ariaLabel: string; danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className={
        "p-2 rounded-as-1 text-ink-3 transition-colors duration-as-1 " +
        (danger ? "hover:bg-flag-tint hover:text-flag" : "hover:bg-paper-2 hover:text-walnut-deep")
      }
    >
      {children}
    </button>
  );
}
